<template>
    <b-modal
        ref="show-modal"
        :title="$t('segment.show_modal.title')"
        cancel-title="Close"
        ok-only
        size="xl"
    >
      <div class="row">
        <div class="col-12">
          <table class="table">
            <tr>
              <th>{{ $t('segment.show_modal.id') }}</th>
              <td>{{ data.id }}</td>
            </tr>
            <tr>
              <th>{{ $t('segment.show_modal.name') }}</th>
              <td>{{ data.name }}</td>
            </tr>
            <tr>
              <th>{{ $t('segment.show_modal.is_static') }}</th>
              <td>{{ data.isStatic }}</td>
            </tr>
            <tr>
              <th>{{ $t('segment.show_modal.createdAt') }}</th>
              <td>{{ data.createdAt | dateTime24HoursUTCFormat }}</td>
            </tr>
            <tr>
              <th>{{ $t('segment.show_modal.updatedAt') }}</th>
              <td>{{ data.updatedAt | dateTime24HoursUTCFormat }}</td>
            </tr>
          </table>

          <b-card
              v-for="item in segmentRules"
              :key="item.id"
              no-body
              border-variant="primary"
              :header="$t(`btn.segment.segment_type.${item.type}`)"
              header-bg-variant="primary"
              header-text-variant="white"
              align="left"
          >
            <table class="table table-striped mb-0">
              <tbody>
                <tr v-if="item.allowedGenders">
                  <th class="w-50">{{ $t('segment.show_modal.allowed_genders') }}</th>
                  <td >{{ item.allowedGenders.join() }}</td>
                </tr>

                <tr v-if="item.agreements">
                  <th class="w-50">{{ $t('segment.show_modal.agreements') }}</th>
                  <td >{{ item.agreements.join() }}</td>
                </tr>

                <tr v-if="item.operation">
                  <th class="w-50">{{ $t('segment.show_modal.operation') }}</th>
                  <td v-if="item.operation === 'IN'">{{ $t('segment.option.in') }}</td>
                  <td v-if="item.operation === 'NOT_IN'">{{ $t('segment.option.not_in') }}</td>
                  <td v-if="item.operation === 'EQ'">{{ $t('segment.option.equal') }}</td>
                  <td v-if="item.operation === 'NEQ'">{{ $t('segment.option.not_equal') }}</td>
                </tr>

                <tr v-if="item.locales">
                  <th class="w-50">{{ $t('segment.show_modal.locales') }}</th>
                  <td >{{ item.locales.join() }}</td>
                </tr>

                <tr v-if="item.countries">
                  <th class="w-50">{{ $t('segment.show_modal.countries') }}</th>
                  <td >{{ item.countries.join() }}</td>
                </tr>

                <tr v-if="item.groupId && allSegments">
                  <th class="w-50">{{ $t('segment.show_modal.group_id') }}</th>
                  <div v-for="segment in allSegments" :key="segment.id">
                    <td v-if="item.groupId === segment.id">{{ segment.name }}</td>
                  </div>
                </tr>

                <tr v-if="item.tags && tagDict">
                  <th class="w-50">{{ $t('segment.show_modal.tags') }}</th>
                  <div v-for="tag in tagDict" :key="tag.id">
                    <td v-if="item.tags.includes(tag.id)">{{ tag.name }}</td>
                  </div>
                </tr>

                <tr v-if="item.currency">
                  <th class="w-50">{{ $t('segment.show_modal.currency') }}</th>
                  <td v-if="item.currency.symbol">{{ item.currency.symbol === 'FUN_COINS' ? $t('segment.option.fun_coins') : $t('segment.option.quiz_coins') }}</td>
                  <td v-if="item.currency && !item.currency.symbol">{{ item.currency === 'FUN_COINS' ? $t('segment.option.fun_coins') : $t('segment.option.quiz_coins') }}</td>
                </tr>

                <tr v-if="item.ruleMoney">
                  <th class="w-50">{{ $t('segment.show_modal.rule_money') }}</th>
                  <table class="table mb-0">
                    <tbody>
                      <tr>
                        <th class="w-50">{{ $t('segment.show_modal.rule_money.amount') }}</th>
                        <td>
                          {{ item.ruleMoney.amount.amount }}
                        </td>
                      </tr>
                      <tr>
                        <th class="w-50">{{ $t('segment.show_modal.rule_money.condition') }}</th>
                        <td v-if="item.ruleMoney.condition === 'GT'">{{ $t('segment.option.greater_than') }}</td>
                        <td v-if="item.ruleMoney.condition === 'GTE'">{{ $t('segment.option.greater_than_or_equal') }}</td>
                        <td v-if="item.ruleMoney.condition === 'LT'">{{ $t('segment.option.less_than') }}</td>
                        <td v-if="item.ruleMoney.condition === 'LTE'">{{ $t('segment.option.less_than_or_equal') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </tr>

                <tr v-if="item.ruleTime">
                  <th class="w-50">{{ $t('segment.show_modal.rule_time') }}</th>
                  <table class="table mb-0">
                    <tbody>
                      <tr v-if="item.ruleTime.dateFrom">
                        <th class="w-50">{{ $t('segment.show_modal.rule_time.date_from') }}</th>
                        <td>{{ item.ruleTime.dateFrom | dateTime24HoursUTCFormat }}</td>
                      </tr>

                      <tr v-if="item.ruleTime.dateTo">
                        <th class="w-50">{{ $t('segment.show_modal.rule_time.date_to') }}</th>
                        <td>{{ item.ruleTime.dateTo | dateTime24HoursUTCFormat }}</td>
                      </tr>

                      <tr v-if="item.ruleTime.daysFrom || item.ruleTime.daysFrom === 0">
                        <th class="w-50">{{ $t('segment.show_modal.rule_time.days_from') }}</th>
                        <td>{{ item.ruleTime.daysFrom }}</td>
                      </tr>

                      <tr v-if="item.ruleTime.daysTo">
                        <th class="w-50">{{ $t('segment.show_modal.rule_time.days_to') }}</th>
                        <td>{{ item.ruleTime.daysTo }}</td>
                      </tr>

                      <tr v-if="item.ruleTime.operation">
                        <th class="w-50">{{ $t('segment.show_modal.rule_time.operation') }}</th>
                        <td>{{ item.ruleTime.operation === 'BETWEEN_DATES' ? $t('segment.option.between_dates') : $t('segment.option.between_days') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </tr>

                <tr v-if="item.ruleAmount">
                  <th class="w-50">{{ $t('segment.show_modal.rule_amount') }}</th>
                  <table class="table table-striped mb-0">
                    <tbody>
                      <tr>
                        <th class="w-50">{{ $t('segment.show_modal.rule_amount.amount') }}</th>
                        <td>
                          {{ item.ruleAmount.amount.amount }}
                        </td>
                      </tr>
                      <tr>
                        <th class="w-50">{{ $t('segment.show_modal.rule_amount.condition') }}</th>
                        <td v-if="item.ruleAmount.condition === 'GT'">{{ $t('segment.option.greater_than') }}</td>
                        <td v-if="item.ruleAmount.condition === 'GTE'">{{ $t('segment.option.greater_than_or_equal') }}</td>
                        <td v-if="item.ruleAmount.condition === 'LT'">{{ $t('segment.option.less_than') }}</td>
                        <td v-if="item.ruleAmount.condition === 'LTE'">{{ $t('segment.option.less_than_or_equal') }}</td>
                      </tr>
                    </tbody>
                  </table>
                </tr>
              </tbody>
            </table>
          </b-card>
        </div>
      </div>
    </b-modal>
</template>

<script>
//TODO: import / load / get locale the right way
import axios from "axios";

let locale = locale ? locale : "en";

export default {
  data() {
    return {
      data: {
        id: null,
      },
      segmentRules: null,
      allSegments: []
    };
  },
  filters: {},
  mounted() {
    this.$store.dispatch('root/getTags');
    this.loadItems();

    this.showModal();
  },
  computed: {
    tagDict: function () {
      return this.$store.getters['root/tag'];
    },
  },
  methods: {
    loadItems() {
      axios.get(`/api/segments?limit=10000&offset=0`)
        .then(response => {
          this.allSegments = response.data.items;
        })
        .catch(error => {
          console.log(error);
        });
    },
    setData(data) {
      this.data = data;
      this.$store.dispatch('userSegment/getSegmentRules', data.id).then(resp => {
        this.segmentRules = resp;
      });
    },
    showModal(){
      this.$refs['show-modal'].show();
    }
  }
}
</script>
